(function(){'use strict';
// Isolated proposal: no listeners, globals, network, storage, or live form edits.
const LIMITS = Object.freeze({ items: 8, context: 1800, attribution: 4000, ttl: 86400000 });
const bytes = value => new TextEncoder().encode(value).length;
const plain = value => value !== null && typeof value === 'object' && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
const exact = (value, keys) => plain(value) && Object.keys(value).sort().join(',') === [...keys].sort().join(',') &&
  Object.values(Object.getOwnPropertyDescriptors(value)).every(d => Object.hasOwn(d, 'value'));
const token = value => typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_:-]{0,79}$/.test(value);
const frozen = value => { if (value && typeof value === 'object') { Object.values(value).forEach(frozen); Object.freeze(value); } return value; };
const itemKeys = ['type', 'key', 'line', 'source', 'content_version'];
const formIds = ['appointment_men', 'appointment_donna', 'appointment_riviera_privee', 'appointment_generic'];

/** Called with the existing producer's already prepared attribution and IDs.
 * A failure requires explicit continuation without selection in a future UI.
 * It never silently proceeds with a claimed-but-missing selection.
 */
function prepareSelectionContext({ baseAttributionRaw, review, eventId, formId, primaryLine,
  catalogNamespace, registryVersion, now }) {
  if (typeof baseAttributionRaw !== 'string') return Object.freeze({ proceed: false, attached: false,
    reason: 'attribution-invalid', requiresContinueWithoutSelection: false, attributionRaw: null });
  const fallback = reason => frozen({ proceed: false, attached: false, reason,
    requiresContinueWithoutSelection: true, attributionRaw: baseAttributionRaw });
  if (review === null || review === undefined) {
    return frozen({ proceed: true, attached: false, reason: 'no-selection',
      requiresContinueWithoutSelection: false, attributionRaw: baseAttributionRaw });
  }
  try {
    if (review.ok !== true || !exact(review.selection, ['catalog_namespace', 'registry_version', 'items'])) return fallback('review-invalid');
    const selected = review.selection;
    if (!Array.isArray(selected.items)) return fallback('review-invalid');
    if (selected.items.length === 0) return frozen({ proceed: true, attached: false, reason: 'empty-explicit-selection',
      requiresContinueWithoutSelection: false, attributionRaw: baseAttributionRaw });
    if (selected.items.length > LIMITS.items) return fallback('item-count');
    if (!Number.isSafeInteger(now) || now < 0 || !Number.isSafeInteger(review.reviewed_at) ||
        !Number.isSafeInteger(review.expires_at) || review.reviewed_at < 0 || review.reviewed_at > now ||
        now >= review.expires_at || review.expires_at - review.reviewed_at > LIMITS.ttl) return fallback('review-expired');
    if (!/^lead_[a-f0-9]{32}$/.test(eventId) || !formIds.includes(formId) || !['men', 'donna'].includes(primaryLine)) return fallback('native-binding-invalid');
    if (!token(catalogNamespace) || !token(registryVersion) || selected.catalog_namespace !== catalogNamespace ||
        selected.registry_version !== registryVersion) return fallback('registry-mismatch');
    const seen = new Set(), items = [];
    for (const value of selected.items) {
      if (!exact(value, itemKeys) || !['product', 'look', 'direction'].includes(value.type) || !token(value.key) ||
          !['men', 'donna'].includes(value.line) || !token(value.source) || !/^[a-f0-9]{16}$/.test(value.content_version) ||
          (value.type === 'product' && !/^[1-9][0-9]{0,19}$/.test(value.key))) return fallback('item-invalid');
      const key = value.type + ':' + value.key;
      if (seen.has(key)) return fallback('duplicate-item');
      seen.add(key); items.push(Object.fromEntries(itemKeys.map(k => [k, value[k]])));
    }
    const base = JSON.parse(baseAttributionRaw);
    if (!plain(base)) return fallback('attribution-invalid');
    // The native producer supplies JSON.stringify output. Require that exact
    // representation before editing, so duplicate keys cannot disappear here.
    if (JSON.stringify(base) !== baseAttributionRaw) return fallback('attribution-not-native-canonical-json');
    const context = { schema: 'cs_booking_context_v2', kind: 'explicit_selection', event_id: eventId, form_id: formId,
      primary_line: primaryLine, catalog_namespace: catalogNamespace, registry_version: registryVersion, items };
    if (Object.hasOwn(base, 'booking_context')) {
      const prior = base.booking_context;
      if (!exact(prior, ['schema', 'kind', 'product_id', 'product_handle', 'product_title', 'product_url', 'event_id', 'form_id']) ||
          prior.schema !== 'cs_booking_context_v1' || prior.kind !== 'product_bound' || prior.event_id !== eventId || prior.form_id !== formId) {
        return fallback('native-page-context-invalid');
      }
      context.page_product = prior;
    }
    if (bytes(JSON.stringify(context)) > LIMITS.context) return fallback('context-limit');
    const output = JSON.stringify({ ...base, booking_context: context });
    if (bytes(output) > LIMITS.attribution) return fallback('attribution-limit');
    return frozen({ proceed: true, attached: true, reason: null, requiresContinueWithoutSelection: false,
      attributionRaw: output, contextBytes: bytes(JSON.stringify(context)), attributionBytes: bytes(output) });
  } catch { return fallback('context-unavailable'); }
}

/** P2 form gate: no network, tracking, storage or submission of its own. */
function createFormCoordinator({ getAPI, liveRegistry, askWithout, prepare, now = Date.now }) {
  const tickets = new WeakSet(), completions=new WeakMap();
  const identity = item => ({type:item.type,key:item.key});
  const fingerprint = state => JSON.stringify({revision:state.revision,created_at:state.created_at,expires_at:state.expires_at,items:state.items});
  const ticket = value => { const result=Object.freeze(value);tickets.add(result);return result; };
  async function fallback(reason) { try { return (await askWithout(reason)) === true; } catch { return false; } }
  async function without(reason) { return (await fallback(reason)) ? ticket({ok:true,without:true}) : Object.freeze({ok:false,reason:'cancelled'}); }
  async function begin() {
    const api=getAPI();
    if(!api?.ready) return without('unavailable');
    let initial;
    try { initial=api.get(); } catch { return without('unavailable'); }
    if(!initial.items.length) return ticket({ok:true,without:false,review:null,fingerprint:fingerprint(initial)});
    let review;
    try { review=await api.review(); } catch { return without('unavailable'); }
    // Closing the review is a cancellation, never an implicit submit without it.
    if(!review?.ok) return Object.freeze({ok:false,reason:review?.reason||'cancelled'});
    const state=api.get();
    const identities=review.selection.items.map(identity);
    const current=api.reviewForForm(identities);
    if(!current.ok || JSON.stringify(current.selection)!==JSON.stringify(review.selection)) return without('changed');
    if(review.selection.items.length) {
      try { const live=await liveRegistry.validate(review.selection.items); if(live?.ok!==true) return without(live?.reason||'unavailable'); }
      catch { return without('unavailable'); }
      if(fingerprint(api.get())!==fingerprint(state)) return without('changed');
    }
    return ticket({ok:true,without:false,review,fingerprint:fingerprint(state)});
  }
  async function commit(value, native) {
    if(!tickets.has(value)) return Object.freeze({proceed:false,reason:'invalid-ticket'});
    tickets.delete(value);
    if(value.without) { const out=Object.freeze({proceed:true,attributionRaw:native.baseAttributionRaw,attached:false});completions.set(out,null);return out; }
    const api=getAPI();
    let result,reason='changed';
    try {
      if(api?.ready && fingerprint(api.get())===value.fingerprint) {
        const review=value.review;
        if(review?.selection.items.length) {
          const current=api.reviewForForm(review.selection.items.map(identity));
          if(!current.ok || JSON.stringify(current.selection)!==JSON.stringify(review.selection)) throw new Error('stale');
        }
        result=prepare({...native,review,now:now()});
        if(result.proceed) { completions.set(result,value.fingerprint);return result; }
        reason=result.reason;
      }
    } catch { reason='unavailable'; }
    if(await fallback(reason)) { const out=Object.freeze({proceed:true,attributionRaw:native.baseAttributionRaw,attached:false});completions.set(out,null);return out; }
    return Object.freeze({proceed:false,reason:'cancelled'});
  }
  function isCurrent(result){if(!completions.has(result))return false;const expected=completions.get(result);completions.delete(result);try{return expected===null||fingerprint(getAPI().get())===expected;}catch{return false;}}
  return Object.freeze({begin,commit,isCurrent});
}

(function installFormBridge(win,doc) {
  if(win.CSInspirationForms) return;
  const config=win.CSInspirationConfig;
  const root=doc.querySelector('[data-cs-inspiration-root]');
  if(config?.enabled!==true || !root) return;
  let copy;
  try { copy=JSON.parse(root.querySelector('[data-cs-inspiration-copy]').textContent).texts; } catch { return; }
  const askWithout=async reason=>win.confirm(reason==='changed'?copy.continue_changed:copy.continue_without);
  const coordinator=createFormCoordinator({getAPI:()=>win.CSInspiration,
    liveRegistry:{validate:items=>win.CSInspirationLiveRegistry?.validate(items)||Promise.resolve({ok:false,reason:'unavailable'})},
    askWithout,prepare:prepareSelectionContext});
  const mounts=new Map();
  function mount() {
    const api=win.CSInspiration;if(!api?.ready) return;
    mounts.forEach((entry,form)=>{if(!form.isConnected){entry.dispose();entry.element.remove();mounts.delete(form);}});
    doc.querySelectorAll('form[data-cs-lead-form]').forEach(form=>{
      if(mounts.has(form)||form.querySelector('.cs-appt-success')) return;
      const submit=form.querySelector('[type="submit"]');if(!submit) return;
      const element=doc.createElement('section');element.className='cs-inspiration__form-summary';
      element.setAttribute('aria-label',copy.title);element.dataset.csInspirationForm='';
      submit.parentNode.insertBefore(element,submit);
      const dispose=api.mountSummary(element);mounts.set(form,{element,dispose});
    });
  }
  win.CSInspirationForms=Object.freeze({
    begin:()=>coordinator.begin(),
    isCurrent:result=>coordinator.isCurrent(result),
    commit:(ticket,native)=>coordinator.commit(ticket,{...native,catalogNamespace:config.registry.catalog_namespace,registryVersion:config.registry.version})
  });
  win.addEventListener('cs:inspiration-ready',mount);
  doc.addEventListener('shopify:section:load',mount);
  doc.addEventListener('shopify:section:unload',event=>{mounts.forEach((entry,form)=>{if(event.target.contains(form)){entry.dispose();entry.element.remove();mounts.delete(form);}});});
  mount();
})(window,document);

})();
